import React, { useMemo } from "react";
import { FieldArray } from "react-final-form-arrays";
import { useSelector } from "react-redux";
import classNames from "classnames";
import Parameter from "../Parameter";
import { ButtonStyle } from "components/Button";
import { FormFields as InputsFormFields } from "../../../InputsToOptimize";
import { checkSomeParameterNotSaved, FormFields, MAX_PARAMETERS, ParameterFormFields } from "../../index";
import SectionHeader from "rs-components/SectionHeader";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { OptimizationTargetType, } from "store/rs-workspace/rs-workspace.types";
import { workspaceCellModelsSelector, workspaceInsightsSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { getOptimizationParameterSectionTargetSource } from "store/rs-workspace/utils/common.utils";
import { isNotEmpty } from "utils/validate";
import { OptimizationLabels } from "shared/labels";
import * as styles from "./styles.module.scss";
const MAX_PARAMETER_PARAMETERS = 5;
const ParameterSection = ({ renderProps, input, name, index, maxParametersReached, restrictions, }) => {
    const { fields } = renderProps;
    const parametersSection = fields.value?.[index];
    const editable = useSelector(isEditableSelector);
    const workspaceInsights = useSelector(workspaceInsightsSelector);
    const workspaceSimulations = useSelector(workspaceSimulationsSelector);
    const workspaceCellModels = useSelector(workspaceCellModelsSelector);
    const maxParameterParametersReached = useMemo(() => (parametersSection?.parameters?.length ?? 0) >= MAX_PARAMETER_PARAMETERS, [parametersSection?.parameters?.length]);
    const selectedInput = useMemo(() => {
        const selectedInsightId = input?.[InputsFormFields.insightId];
        const selectedInsightInputSetId = input?.[InputsFormFields.insightInputSetId];
        if (!isNotEmpty(parametersSection) ||
            !isNotEmpty(selectedInsightId) ||
            !isNotEmpty(selectedInsightInputSetId)) {
            return undefined;
        }
        const selectedInsight = workspaceInsights.find(({ id }) => id === selectedInsightId);
        return selectedInsight?.state?.input?.inputSets
            ?.find(({ id }) => id === selectedInsightInputSetId)
            ?.inputs?.find(({ id }) => id === parametersSection.inputId);
    }, [input, parametersSection, workspaceInsights]);
    const sectionTargetSource = useMemo(() => {
        if (!isNotEmpty(selectedInput)) {
            return undefined;
        }
        return getOptimizationParameterSectionTargetSource(selectedInput, workspaceSimulations);
    }, [selectedInput, workspaceSimulations]);
    const refinedCellModelIsATarget = useMemo(() => {
        const optimizationTargetType = input?.[InputsFormFields.optimizationTarget];
        if (optimizationTargetType !== OptimizationTargetType.CELL_MODEL) {
            return false;
        }
        const targetCellModel = workspaceCellModels.find(({ id }) => sectionTargetSource?.source.cellModelId === id);
        return targetCellModel?.refined;
    }, [input, sectionTargetSource?.source.cellModelId, workspaceCellModels]);
    const someParameterNotSaved = useMemo(() => checkSomeParameterNotSaved(fields.value || []), [fields.value]);
    const plusButtonDisabledTooltip = useMemo(() => {
        if (refinedCellModelIsATarget) {
            return OptimizationLabels.NO_REFINED_CELL_MODEL_PARAMETERS;
        }
        if (someParameterNotSaved) {
            return OptimizationLabels.IS_NOT_SAVED_PARAMETER.replace("#TYPE", input?.[InputsFormFields.optimizationTarget] === OptimizationTargetType.RECIPE
                ? "Recipe"
                : "Cell Model");
        }
        if (maxParametersReached || maxParameterParametersReached) {
            return `${OptimizationLabels.MAX_PARAMETERS} ${maxParametersReached ? MAX_PARAMETERS : MAX_PARAMETER_PARAMETERS}.`;
        }
    }, [
        input,
        maxParameterParametersReached,
        maxParametersReached,
        refinedCellModelIsATarget,
        someParameterNotSaved,
    ]);
    const customAddParameterMessage = useMemo(() => {
        const optimizationTargetType = input?.[InputsFormFields.optimizationTarget];
        if (optimizationTargetType === OptimizationTargetType.RECIPE) {
            return "Add recipe parameter";
        }
        else if (optimizationTargetType === OptimizationTargetType.CELL_MODEL) {
            return "Add cell model parameter";
        }
    }, [input]);
    const sectionTitle = useMemo(() => {
        if (sectionTargetSource === undefined) {
            return "Not available";
        }
        if (sectionTargetSource.adjustmentId === 0) {
            return `${selectedInput?.id}. ${sectionTargetSource.source.name}`;
        }
        return `${selectedInput?.id}. ${sectionTargetSource.source.name}_${sectionTargetSource.source.state.adjustments?.find(({ id }) => id === sectionTargetSource.adjustmentId)
            ?.name ?? ""}`;
    }, [sectionTargetSource, selectedInput]);
    return (React.createElement("div", { key: `${name}-${index}-${fields.length}`, className: styles.parameterSection },
        React.createElement(FieldArray, { name: `${name}.${FormFields.parameters}`, render: (renderProps) => (React.createElement(React.Fragment, null,
                React.createElement(SectionHeader, { title: sectionTitle, editable: editable && sectionTargetSource !== undefined, mainAction: {
                        handler: () => renderProps.fields.push({
                            [ParameterFormFields.notSaved]: true,
                            [ParameterFormFields.parameterId]: undefined,
                        }),
                        disabled: !!plusButtonDisabledTooltip,
                        disabledTooltip: plusButtonDisabledTooltip,
                        customAddMessage: customAddParameterMessage,
                        style: !parametersSection?.parameters?.length ? ButtonStyle.Primary : ButtonStyle.Secondary,
                    }, notAvailable: sectionTargetSource === undefined }),
                React.createElement("div", { className: classNames(styles["parameterSection-content"], {
                        [styles.empty]: !parametersSection?.parameters?.length,
                    }) },
                    renderProps.fields.map((name, index) => (React.createElement(Parameter, { key: `${name}-${index}-${renderProps.fields.length}`, name: name, renderProps: renderProps, index: index, targetSource: sectionTargetSource, targetType: input?.[InputsFormFields.optimizationTarget], restrictions: restrictions }))),
                    !parametersSection?.parameters?.length && (React.createElement("div", { className: styles.emptyHint },
                        React.createElement("h4", null, OptimizationLabels.NO_PARAMETERS_TO_OPTIMIZE_HEADER),
                        React.createElement("p", null, OptimizationLabels.NO_PARAMETERS_TO_OPTIMIZE_TEXT)))))) })));
};
export default ParameterSection;
